import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { workspaceApi, type Message } from '../api/workspace';
import { EmptyState, Loading, PageError } from '../components/State';
import { fmtMsgTime, initials } from '../lib/format';
import './MessagesPage.css';

type Row = {
  connectionId: string;
  youthName: string;
  last: Message | null;
  unread: number;
};

// Mirrors the native CTMessagesScreen: there is no list endpoint, so we fan out
// GET /:connectionId/messages per connection. That endpoint also marks the
// caregiver's messages as read — same behaviour as the app.
async function loadThreads(): Promise<Row[]> {
  const clients = await workspaceApi.clients();
  const rows = await Promise.all(
    clients.map(async (c) => {
      const youthName = c.youthSnapshot?.name ?? 'Youth';
      try {
        const msgs = await workspaceApi.messages(c.connectionId);
        return {
          connectionId: c.connectionId,
          youthName,
          last: msgs[msgs.length - 1] ?? null,
          unread: msgs.filter((m) => m.senderRole === 'caregiver' && !m.readAt).length,
        };
      } catch {
        return { connectionId: c.connectionId, youthName, last: null, unread: 0 };
      }
    }),
  );
  return rows.sort((a, b) => (b.last?.createdAt ?? '').localeCompare(a.last?.createdAt ?? ''));
}

export function MessagesPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['threads'],
    queryFn: loadThreads,
  });

  if (isLoading) return <Loading />;

  return (
    <div>
      <h1>Messages</h1>
      {isError || !data ? (
        <PageError error={error} onRetry={() => refetch()} />
      ) : data.length === 0 ? (
        <EmptyState text="No connected families yet." />
      ) : (
        <div className="card msg-list">
          {data.map((r) => {
            const mine = r.last?.senderRole === 'care_team';
            const preview = r.last
              ? `${mine ? 'You: ' : ''}${r.last.text}`
              : 'No messages yet · start the conversation';
            return (
              <Link
                key={r.connectionId}
                to={`/messages/${r.connectionId}`}
                state={{ youthName: r.youthName }}
                className="msg-list__row"
              >
                <div className={`msg-list__avatar${r.unread ? ' is-unread' : ''}`}>
                  {initials(r.youthName)}
                </div>
                <div className="msg-list__main">
                  <div className="msg-list__top">
                    <span className={`msg-list__name${r.unread ? ' is-unread' : ''}`}>
                      {r.youthName}
                    </span>
                    <span className="muted msg-list__time">
                      {fmtMsgTime(r.last?.createdAt)}
                    </span>
                  </div>
                  <div className={`msg-list__preview${r.unread ? ' is-unread' : ''}`}>
                    {preview}
                  </div>
                </div>
                {r.unread > 0 && <span className="msg-list__badge">{r.unread}</span>}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
